import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import reduxUtils from 'util/redux';
import classnames from 'classnames';
import { debounce, noop } from 'lodash';
import tracking from 'tracking';
import { CART_STEP_1 } from 'tracking/constants';
import { CHECKOUT } from 'constants/page-names';
import Aria51 from 'components/aria-51';
import { CART_FOOTER_DEBOUNCE } from 'constants/debounce-delays';
import preventDefault from 'util/dom/prevent-default';

import formatPrice from 'util/misc/format-price';
import CartProductsList from 'components/cart-products-list';
import t, { Trans } from 'translations';
import Button from 'components/button';
import Empty from './empty';
import CmsPlaceholder from 'components/cms-placeholder';

import Icon from 'components/icon';
import Tooltip from 'components/tooltip';
import OrderPromotionList from 'components/order-promotion-list';

const tabIndexMap = {
    close: 1,
    clear: 2,
    checkoutButton: 3,
    content: 4,
};

function CartFooter({ facet, estimatedCustomerPrice, customerDeliveryFee, hasMissingMloginData, isAuthenticated, hasItems, login, routerRegistry }) {
    return (
        <footer className={`cart--footer cart--footer__${ facet }`}>
            <div className="cart--total">
                <dl className="cart--total-inner">
                    <dt className="cart--total-text"><Trans path={'cart.total'}/></dt>
                    <dd className="cart--total-shipping">
                        <Trans path={'cart.shipping_cost'}/> {formatPrice(customerDeliveryFee)}
                    </dd>
                    <dd className="cart--total-amount">CHF {formatPrice(estimatedCustomerPrice)}</dd>
                </dl>
            </div>
            <Button tag="a"
                tabIndex={tabIndexMap.checkoutButton}
                to={routerRegistry.get(CHECKOUT).url}
                onClick={(e) => {
                    // block the link in case the user is not allowed to navigate to the checkout page
                    // @TODO:  move this logic in a reducer in case we will have other buttons or links pointing to this page
                    if (!isAuthenticated || hasMissingMloginData) {
                        preventDefault(e);
                        login(window.location.href.replace(window.location.pathname, routerRegistry.get(CHECKOUT).url));
                    }
                }}
                className="cart--continue"
                size="large"
                facet="primary"
                disabled={!hasItems}>
                <Trans path={'cart.continue'}/>
            </Button>
        </footer>
    );
}

CartFooter.propTypes = {
    login: PropTypes.func.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    facet: PropTypes.string.isRequired,
    hasMissingMloginData: PropTypes.bool,
    customerDeliveryFee: PropTypes.string.isRequired,
    estimatedCustomerPrice: PropTypes.string.isRequired,
    hasItems: PropTypes.bool.isRequired,
    routerRegistry: PropTypes.object.isRequired,
};

function Faq({ onLoaded }) {
    return (
        <div className="cart--faq">
            <CmsPlaceholder onEvent={onLoaded} onLoaded={onLoaded} useRandomIconsLoader={false} id="cart_faq"
                facet="cart-panel"/>
        </div>
    );
}

Faq.propTypes = {
    onLoaded: PropTypes.func.isRequired,
};

Faq.defaultProps = {
    onLoaded: noop,
};

function TooltipContent() {
    return (
        <div>
            <h3 style={{ marginBottom: 4 }}>
                <Trans path="checkout.tooltip.title"/>
            </h3>
            <p>
                <Trans path="checkout.tooltip.content"/>
            </p>
        </div>
    );
}

function CartContents({ tabIndex, items, bagInfo, shippingPrice, promotions, onFaqLoaded }) {
    const tooltipActions = [{
        name: 'OK',
        event() {
            // noop
        },
    }];


    return (
        <div className="cart--content-inner">
            <CartProductsList tabIndex={tabIndex} products={items}/>
            <div className="cart--overview">
                <div className="cart--overview-list">
                    <dl className="cart--overview-item">
                        <dt className="cart--overview-item-term">
                            <Trans
                                path="checkout.panel.number_and_value_of_bags"
                                data={{ amount: bagInfo.total_bags }}
                                amount={bagInfo.total_bags}
                            />&nbsp;
                            {formatPrice(bagInfo.unit_price)}
                        </dt>
                        <dd className="cart--overview-item-definition">
                            {formatPrice(bagInfo.total_bag_price)}
                        </dd>
                    </dl>
                    <OrderPromotionList promotions={promotions} facet='cart'/>
                    <dl className="cart--overview-item">
                        <dt className="cart--overview-item-term">
                            <Trans path={'cart.shipping_costs'}/>
                        </dt>
                        <dd className="cart--overview__delivery-cost cart--overview-item-definition">
                            <Tooltip className="cart-overview--tooltip"
                                content={<TooltipContent/>}
                                interactionType="click"
                                actions={tooltipActions}>
                                <Icon symbol="information" size="large"/>
                            </Tooltip>

                            {shippingPrice}
                        </dd>
                    </dl>
                </div>
            </div>
            <Faq onLoaded={onFaqLoaded}/>
        </div>
    );
}

CartContents.propTypes = {
    tabIndex: PropTypes.number.isRequired,
    items: PropTypes.object.isRequired,
    bagInfo: PropTypes.object.isRequired,
    shippingPrice: PropTypes.string.isRequired,
    promotions: PropTypes.array.isRequired,
    onFaqLoaded: PropTypes.func.isRequired,
};

function CartEmpty({ onFaqLoaded }) {
    return (
        <div className="cart--content-inner">
            <Empty/>
            <Faq onLoaded={onFaqLoaded}/>
        </div>
    );
}

CartEmpty.propTypes = {
    onFaqLoaded: PropTypes.func.isRequired,
};

class Cart extends PureComponent {
    static propTypes = {
        items: PropTypes.object.isRequired,
        isFetching: PropTypes.bool.isRequired,
        closePanel: PropTypes.func.isRequired,
        clearCart: PropTypes.func.isRequired,
        updateFooterState: PropTypes.func.isRequired,
        scrollbarWidth: PropTypes.number.isRequired,
        bagInfo: PropTypes.object.isRequired,
        shippingPrice: PropTypes.string.isRequired,
        customerDeliveryFee: PropTypes.string.isRequired,
        estimatedCustomerPrice: PropTypes.string.isRequired,
        viewportHeight: PropTypes.number.isRequired,
        viewportMqQuery: PropTypes.string.isRequired,
        isCartEmpty: PropTypes.bool.isRequired,
        isFooterFixed: PropTypes.bool.isRequired,
        hasMissingMloginData: PropTypes.bool,
        routerRegistry: PropTypes.object.isRequired,
        isAuthenticated: PropTypes.bool.isRequired,
        promotions: PropTypes.array.isRequired,
        login: PropTypes.func.isRequired,
    };

    static defaultProps = {
        promotions: [],
    };

    constructor(props) {
        super(props);
        this.state = {
            isScrolling: false,
        };
        this.scrollTimer = null;
    }

    componentDidMount() {
        tracking.checkout({
            step: CART_STEP_1,
            value: this.props.estimatedCustomerPrice,
            products: this.props.items,
        });

        this.setFooterState();
    }

    componentDidUpdate({ items, viewportHeight }) {
        if (this.props.viewportHeight !== viewportHeight || Object.keys(items).length !== Object.keys(this.props.items).length) {
            this.setFooterState();
        }
    }

    componentWillUnmount() {
        clearTimeout(this.scrollTimer);
        this.setFooterState.cancel();
    }

    onContentScroll = () => {
        clearTimeout(this.scrollTimer);

        if (!this.state.isScrolling) {
            this.setState({
                isScrolling: true,
            });
        }

        this.scrollTimer = setTimeout(() => {
            this.setState({
                isScrolling: false,
            });
        }, 66);
    };

    setFooterState = debounce(function() {
        const { content } = this.refs;
        if (content && content.children) {
            this.props.updateFooterState(content.children[0]);
        }
    }, CART_FOOTER_DEBOUNCE);

    get actions() {
        return [{
            name: <Trans path={'tooltip.cancel'}/>,
        }, {
            name: <Trans path={'tooltip.confirm'}/>,
            isHighlighted: true,
            event: () => {
                this.props.clearCart();
            },
        }];
    }

    get scrollbarOffset() {
        const { scrollbarWidth } = this.props;
        return { right: `${ scrollbarWidth }px`, width: `calc(100% - ${ scrollbarWidth }px)` };
    }

    render() {
        const {
            isFooterFixed,
            login,
            closePanel,
            hasMissingMloginData,
            isAuthenticated,
            isFetching,
            estimatedCustomerPrice,
            shippingPrice,
            customerDeliveryFee,
            isCartEmpty,
            routerRegistry,
        } = this.props;
        const { isScrolling } = this.state;
        const hasItems = !isCartEmpty;

        return (
            <Aria51
                component="section"
                className={classnames('cart', {
                    'cart__is-fetching': isFetching,
                    'cart__is-footer-fixed': isFooterFixed,
                    'cart__is-scrolling': isScrolling,
                })}>
                <header className="cart--header">
                    <div className="cart--heading">
                        <h1 className="cart--title"><Trans path={'cart.cart'}/></h1>
                        {hasItems && <div className="cart--clear-wrapper">
                            <Tooltip interactionType="click" content={<Trans path={'cart.clear_cart_question'}/>}
                                actions={this.actions}>
                                <button className="cart--clear" tabIndex={tabIndexMap.clear}
                                    aria-label={t('cart.clear_cart')}>
                                    <Trans path={'cart.clear_cart'}/>
                                </button>
                            </Tooltip>
                        </div>
                        }
                    </div>
                    <button className="cart--close" tabIndex={tabIndexMap.close}
                        aria-label={t('accessibility.label.close_cart')} onClick={closePanel}>
                        <Icon symbol="cancel" size="medium"/>
                    </button>
                </header>
                <div onScroll={this.onContentScroll} className="cart--content" ref="content">
                    {hasItems ?
                        <CartContents
                            onFaqLoaded={this.setFooterState.bind(this)}
                            tabIndex={tabIndexMap.content} {...this.props}/> :
                        <CartEmpty
                            onFaqLoaded={this.setFooterState.bind(this)}/>}
                    {!isFooterFixed && <CartFooter
                        routerRegistry={routerRegistry}
                        facet={'inline'}
                        login={login}
                        hasItems={hasItems}
                        hasMissingMloginData={hasMissingMloginData}
                        isAuthenticated={isAuthenticated}
                        estimatedCustomerPrice={estimatedCustomerPrice}
                        customerDeliveryFee={customerDeliveryFee}/>}
                </div>
                {isFooterFixed &&
                <CartFooter
                    routerRegistry={routerRegistry}
                    facet={'fixed'}
                    login={login}
                    hasItems={hasItems}
                    hasMissingMloginData={hasMissingMloginData}
                    isAuthenticated={isAuthenticated}
                    estimatedCustomerPrice={estimatedCustomerPrice}
                    customerDeliveryFee={customerDeliveryFee}
                    shippingPrice={shippingPrice}
                />}
            </Aria51>
        );
    }
}

export default connect(
    reduxUtils.mapStateToProps({
        items: 'cart.items',
        bagInfo: 'cart.bagInfo',
        isFetching: 'cart.isFetching',
        scrollbarWidth: 'scroll.scrollbarWidth',
        shippingPrice: 'cart.shippingPrice',
        isAuthenticated: 'user.isAuthenticated',
        hasMissingMloginData: 'user.has_missing_mlogin_data',
        customerDeliveryFee: 'cart.customerDeliveryFee',
        estimatedCustomerPrice: 'cart.estimatedCustomerPrice',
        viewportHeight: 'viewport.height',
        viewportMqQuery: 'viewport.mq.query',
        isCartEmpty: 'cart.isEmpty',
        routerRegistry: 'router.registry',
        cartFooterState: 'cart.registry',
        isFooterFixed: 'cart.isFooterFixed',
        promotions: 'cart.promotions',
    }),
    reduxUtils.mapDispatchToProps({
        closePanel: 'panel.close',
        clearCart: 'cart.clearCart',
        login: 'user.login',
        updateFooterState: 'cart.updateFooterState',
    }),
)(Cart);



// WEBPACK FOOTER //
// ./mcart/assets/src/js/app/components/cart/index.js